import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';

import { AppContext } from '../../../../../RoutesWithAuth';

import axios from '../../../../common/AxiosConfig';

import { LoadingIndicator } from '../../reports/reconcilationreport/MuiStyled';

import CustomGrid from '../../customgrid';

import EditDialog from '../../customgrid/EditDialog';

import { makeStyles } from "@mui/styles";

import { IconButton, Tooltip } from '@mui/material';

import { Edit } from '@mui/icons-material';

import { getFormatedDate, v2UIRoutePath } from '../../../../common/commons';
import { ADMIN_PERMISSIONS } from '../../../../common/RolePermissions';
import PermissionButton from '../../PermissionButton';

const RetentionOptions = ['30', '60', '90', '365', '730', '1095', 'Forever'];

const EDIT_FIELDS = [
    { name: 'requestor', label: 'Setting', type: 'text', disabled: true },
    {
        name: 'retention_period', label: 'Retention in Days', type: 'select',
        options: RetentionOptions,
        required: true
    }
];

const useStyles = makeStyles(() => ({
    retentionWrapper: {
        marginTop: '4px',
        marginRight: '15px'
    },
    noteWrapper: {
        marginTop: '16px',
        marginLeft: '4px',
        fontSize: '13px',
        lineHeight: '1.5'
    },
    noteLabel: {
        fontWeight: 'bold'
    }
}));

function Retention() {
    const context = useContext(AppContext)
    const authToken = context.authToken.get
    const isAdmin = context.isAdmin.get
    const classes = useStyles()
    const [appRetention, setAppRetention] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken])

    const fetchData = () => {
        setIsLoading(true);
        axios.get(`appticket_retention`, {
            headers: {
                Authorization: authToken
            }
        }).then(res => {
            setIsLoading(false);
            const data = res.data || [];
            const sorted = [...data].sort((a, b) =>
                (a.requestor || '').localeCompare(b.requestor || '')
            );
            setAppRetention(sorted.map((rec, index) => ({
                id: index + 1,
                ...rec,
                lastUpdatedDateTimeLocal: getFormatedDate(rec.last_updated, false) || "--",
                lastUpdatedDateTimeUTC: getFormatedDate(rec.last_updated, true) || "--",
            })))
        }).catch(err => {
            setIsLoading(false);
            console.log(err)
        })
    }

    const saveRetentionData = (retention) => {
        setIsLoading(true);
        const { id, last_updated, ...other } = retention;
        axios.post(`appticket_retention/update`, {
            ...other,
        }, {
            headers: {
                Authorization: authToken
            }
        })
            .then(res => {
                fetchData()
                setIsLoading(false);

            }).catch(err => {
                console.log(err)
                setIsLoading(false);
            })
    }

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setEditDialogOpen(true);
    }, []);

    const handleRowClick = useCallback(({ row }) => {
        setSelectedRow(row);
    }, []);

    const handleEditSave = useCallback((formValues, originalRow) => {
        saveRetentionData({ ...originalRow, ...formValues });
        setEditDialogOpen(false);
        setEditingRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const columns = useMemo(() => {
        const cols = [{
            name: "#",
            mapping: "id",
            width: 40,
            sortable: false,
            disableColumnMenu: true,
            align: 'center'
        }, {
            name: "Setting",
            mapping: "requestor",
            width: 180,
            sortable: true,
            disableColumnMenu: true
        }, {
            name: "Retention in Days",
            mapping: "retention_period",
            width: 150,
            sortable: true,
            disableColumnMenu: true
        }, {
            name: "Updated By",
            mapping: "updated_by",
            width: 160,
            sortable: true,
            disableColumnMenu: true
        }, {
            name: "Updated On (UTC)",
            mapping: "last_updated",
            width: 200,
            sortable: true,
            renderCell: (params) => (
                    <Tooltip title={params.row["lastUpdatedDateTimeLocal"]}>
                        <span>{params.row["lastUpdatedDateTimeUTC"]}</span>
                    </Tooltip>
                ),
            disableColumnMenu: true
        }];

            cols.push({
                name: "Edit",
                mapping: "actions",
                width: 80,
                sortable: false,
                disableColumnMenu: true,
                align: 'center',
                renderCell: ({ row }) => (
                    <Tooltip title="Edit">
                        <PermissionButton
                            iconOnly
                            permission={ADMIN_PERMISSIONS.SYSTEM_SETTINGS_RETENTION_EDIT}
                            size="small"
                            data-testid="edit"
                            onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}
                        >
                            <Edit fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });

        return cols;
    }, [handleEditClick]);

    return (
        <div className={classes.retentionWrapper}>
            {isLoading && (<LoadingIndicator />)}

            <CustomGrid
                columns={columns}
                rows={appRetention}
                showFilterButton={false}
                onCellClick={handleRowClick}
                activeRowId={selectedRow ? selectedRow.id : null}
            />
            {selectedRow && (
                <div className={classes.noteWrapper}>
                    <span className={classes.noteLabel}>Note: </span>
                    <span>{selectedRow.note}</span>
                </div>
            )}
            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={EDIT_FIELDS}
                title="Edit Retention Setting"
            />
        </div>
    );
}

export default React.memo(Retention);
